import { useCallback, useState } from 'react';

import axios from 'axios';

import { calculateStaffing } from '../dispatcher_layout';
import { OrphanShiftCutoffUser } from '../dispatcher_orphan_shift_dialog';
import { formatDispatcherSaveError } from '../dispatcher_save_errors';
import { buildSavePayloadFromEditorData } from '../dispatcher_save_payload';
import { DispatcherData } from '../dispatcher_types';
import { syncAutoDispatchPermissions } from '../sync_auto_dispatch_permissions';
import { useDispatcherError } from './use_dispatcher_error';

type UseDispatcherSaveOptions = {
  onSaved: () => void;
};

type SaveResponse = {
  orphan_shift_cutoff_users?: OrphanShiftCutoffUser[];
};

export type DispatcherOrphanShiftState = {
  users: OrphanShiftCutoffUser[];
  regionId: number;
  date: string;
} | null;

export const useDispatcherSave = ({ onSaved }: UseDispatcherSaveOptions) => {
  const { showError, confirmation } = useDispatcherError();
  const [saving, setSaving] = useState(false);
  const [resolvingOrphanShifts, setResolvingOrphanShifts] = useState(false);
  const [orphanShift, setOrphanShift] = useState<DispatcherOrphanShiftState>(null);

  const finishSave = useCallback(() => {
    setOrphanShift(null);
    onSaved();
  }, [onSaved]);

  const save = useCallback(
    async ({
      editorData,
      baselineData,
      publish,
      date,
      regionId,
    }: {
      editorData: DispatcherData;
      baselineData: DispatcherData;
      publish: boolean;
      date: string;
      regionId: number;
    }) => {
      if (saving) {
        return;
      }

      setSaving(true);

      try {
        await syncAutoDispatchPermissions(editorData, baselineData);

        const staffing = calculateStaffing(editorData.dispatches, editorData.fieldShiftAssignments);
        const payload = buildSavePayloadFromEditorData(editorData, publish, date, regionId, staffing);
        const response = await axios.patch<SaveResponse>('/bulk/dispatch.json', payload);
        const users = response.data?.orphan_shift_cutoff_users ?? [];

        if (users.length === 0) {
          finishSave();
          return;
        }

        setOrphanShift({ users, regionId, date });
      } catch (error) {
        showError(formatDispatcherSaveError(error));
      } finally {
        setSaving(false);
      }
    },
    [finishSave, saving, showError],
  );

  const keepOrphanShifts = useCallback(() => {
    finishSave();
  }, [finishSave]);

  const removeOrphanShifts = useCallback(async () => {
    if (!orphanShift || resolvingOrphanShifts) {
      return;
    }

    setResolvingOrphanShifts(true);

    try {
      await axios.post('/bulk/dispatch/resolve_orphan_shifts.json', {
        region_id: orphanShift.regionId,
        date: orphanShift.date,
        user_ids: orphanShift.users.map((user) => user.user_id),
      });
      finishSave();
    } catch (error) {
      showError(formatDispatcherSaveError(error));
    } finally {
      setResolvingOrphanShifts(false);
    }
  }, [finishSave, orphanShift, resolvingOrphanShifts, showError]);

  return {
    saving,
    save,
    orphanShift,
    keepOrphanShifts,
    removeOrphanShifts,
    resolvingOrphanShifts,
    confirmation,
  };
};
